import React, { useContext, useEffect, useState } from "react";
import { Text, CustomTable } from "react_reusable_components";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { useIntl } from "react-intl";

import TableActions from "../../components/TableActions";
import useResponsive from "../../core/hooks/useResponsive";
import UserListingTopContainer from "../UserListingTopContainer";
import ActionWithReasonModal from "../ActionWithReasonModal/ActionWithReasonModal";
import UserService from "../../services/user-service";
import { columns } from "./UserListingColumns";
import { TwoRow } from "../../core/layouts";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { UserContext } from "../../globalContext/Users/userProvider";
import { setUserQuery } from "../../globalContext/Users/userActions";
import { recordsPerPageOptions, PAGE_LIMIT, userRole } from "../../constants";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import Styles from "./UserListingContainer.module.scss";
import variables from "../../themes/base/styles/variables";
import { showNotification } from "../../UtilityFunctions/notification";

const UserListingContainer = () => {
  const responsive = useResponsive();
  const [userState, userDispatch] = useContext(UserContext);
  const [authState] = useContext(AuthContext);

  const loaderDispatch = useContext(GlobalLoaderContext)[1];

  const [query, setQuery] = useState({
    page: userState?.query?.page || 1,
    recordsPerPage: userState?.query?.recordsPerPage || PAGE_LIMIT,
    searchStr: userState?.query?.searchStr || "",
  });
  const [filter, setFilter] = useState({
    productCategories: userState?.query?.productCategories || [],
    subCategories: userState?.query?.subCategories || [],
    roles: userState?.query?.roles || [],
    countries: userState?.query?.countries || [],
  });

  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeRecord, setActiveRecord] = useState({});

  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    setSelectedRow(null);
  }, [userState?.userList?.result]);

  useEffect(() => {
    let queryParams = getUserListQueryParams(query, filter);
    userDispatch(setUserQuery({ ...userState?.query, ...query, ...filter }));
    const debouncedApiCall = debounce(() => {
      UserService.getUsersList({
        dispatch: userDispatch,
        loaderDispatch: loaderDispatch,
        params: queryParams,
        intl,
      });
    }, 400);

    debouncedApiCall();
  }, [query, filter]);

  const tableActionsOptions = (record) => {
    let options = [
      {
        label: getLocalizedMessage(intl, "actionsOption.viewDetails"),
        icon: <EyeOutlined className={Styles["icon-style"]} />,
        action: () => navigate(`/view-user-details/${record?.id}`),
      },
    ];
    if (
      authState?.currentRole?.id !== userRole.CA ||
      !record.roles.filter(
        (roleItem) => roleItem.roleId <= authState?.currentRole?.id
      )?.length
    ) {
      options = [
        ...options,
        {
          label: getLocalizedMessage(intl, "actionsOption.editDetails"),
          icon: <EditOutlined className={Styles["icon-style"]} />,
          action: () => navigate(`/edit-user/${record?.id}`),
        },
        {
          label: (
            <span style={{ color: variables.error }}>
              {getLocalizedMessage(intl, "actionsOption.blockUser", {
                blockOrUnblock: record.isActive
                  ? getLocalizedMessage(intl, "userManagement.block")
                  : getLocalizedMessage(intl, "userManagement.unBlock"),
              })}
            </span>
          ),
          icon: (
            <img
              alt="block"
              src={"/icons/block.svg"}
              className={Styles["icon-style"]}
            />
          ),
          action: () => {
            setActiveRecord(record);
            setOpenModal(true);
          },
        },
      ];
    }
    return options;
  };

  function onBlockUnblockClick(remark, setDisabled) {
    UserService.blockUnblockUser({
      body: { ...(remark && { remark }), isActive: !activeRecord.isActive },
      intl,
      userId: activeRecord.id,
      name: activeRecord.name,
    })
      .then(() => {
        setDisabled(false);
        setOpenModal(false);
        let queryParams = getUserListQueryParams(query, filter);
        userDispatch(
          setUserQuery({ ...userState?.query, ...query, ...filter })
        );
        UserService.getUsersList({
          dispatch: userDispatch,
          params: queryParams,
          loaderDispatch: loaderDispatch,
          intl,
        });
      })
      .catch(() => {
        setDisabled(false);
      });
  }

  const downloadUserReportsHandler = () => {
    let queryParams = getUserListQueryParams(query, filter);
    delete queryParams.pageLimit;
    delete queryParams.pageSkip;
    if (userState?.userList?.result?.length)
      UserService.getDownloadUsersReports({
        params: queryParams,
        intl,
        loaderDispatch: loaderDispatch,
      });
    else
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "reports.cannotGenerateData")
      );
  };

  return (
    <>
      {openModal && (
        <ActionWithReasonModal
          onClose={() => setOpenModal(false)}
          open={openModal}
          buttonText={
            activeRecord.isActive
              ? "userManagement.button.block"
              : "userManagement.button.unBlock"
          }
          onSubmit={onBlockUnblockClick}
          isBlock={activeRecord.isActive}
          placeholder={"userManagement.button.addRemark"}
          title={getLocalizedMessage(
            intl,
            "userManagement.button.blockUnblockDesc",
            {
              blockOrUnblock: activeRecord.isActive ? "block" : "unblock",
              name: activeRecord.name,
            }
          )}
        />
      )}
      <TwoRow
        className={Styles["main-container"]}
        topSection={
          <UserListingTopContainer
            query={query}
            setQuery={setQuery}
            filter={filter}
            setFilter={setFilter}
            downloadUserReportsHandler={downloadUserReportsHandler}
          />
        }
        bottomSection={
          <CustomTable
            locale={{
              emptyText: !userState?.userList?.result && (
                <div style={{ minHeight: variables.customTableHeight }}></div>
              ),
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if (record?.id === selectedRow) setSelectedRow(null);
                  else setSelectedRow(record.id);
                },
              };
            }}
            rowClassName={(record) =>
              !record?.isActive && Styles["inactive-row"]
            }
            sticky={{ offsetHeader: 0 }}
            isMobile={!responsive.isSm}
            columns={[
              ...columns(intl, responsive.isMd),
              {
                title: <></>,
                dataIndex: "operation",
                key: "operation",
                align: "center",
                width: responsive.isMd && "5%",
                render: (_, record) => {
                  //NOSONAR
                  return (
                    <TableActions
                      openPopover={selectedRow === record.id}
                      tableActionsOptions={tableActionsOptions(record)}
                    />
                  );
                },
              },
            ]}
            dataSource={userState?.userList?.result?.map((data, idx) => ({
              ...data,
              key: idx,
            }))}
            query={query}
            setQuery={setQuery}
            totalCount={parseInt(userState?.userList?.totalCount)}
            prevText={getLocalizedMessage(intl, "pagination.prevText")}
            nextText={getLocalizedMessage(intl, "pagination.nextText")}
            sizeChangeOptions={recordsPerPageOptions.map((rec) => ({
              value: rec,
              label: (
                <Text className={Styles["select-item-style"]}>
                  {rec}&nbsp;
                  {getLocalizedMessage(intl, "pagination.selectLabelSuffix")}
                </Text>
              ),
            }))}
            tableStyleClassName={`${Styles["custom-table-style"]} ${
              Styles[!responsive?.isSm && "custom-table-mobile-style"]
            } `}
          />
        }
      />
    </>
  );
};

export default UserListingContainer;

function getUserListQueryParams(query, filter) {
  let queryParams = {
    pageLimit: query.recordsPerPage,
    pageSkip: query.recordsPerPage * (query.page - 1),
  };
  if (query.searchStr.length) {
    queryParams = {
      ...queryParams,
      searchStr: encodeURI(query.searchStr),
    };
  }
  if (filter.productCategories.length > 0) {
    queryParams = {
      ...queryParams,
      productCategoryId: filter.productCategories.join(","),
    };
  }
  if (filter.subCategories.length > 0) {
    queryParams = {
      ...queryParams,
      subProductCategoryId: filter.subCategories.join(","),
    };
  }
  if (filter.roles.length > 0) {
    queryParams = {
      ...queryParams,
      roleId: filter.roles.join(","),
    };
  }
  if (filter.countries.length > 0) {
    queryParams = {
      ...queryParams,
      countryId: filter.countries.join(","),
    };
  }
  return queryParams;
}
